// İSG Portal — TableToolbar (S33 planı §1/Stage 2, yeni): `apps/web-expert`'in app-local
// `components/admin/ListToolbar.tsx`'inin (18 tüketici) paylaşılan/Carbon-stilli hedefi.
// BU DİLİMDE `ListToolbar` SİLİNMEZ, hiçbir tüketici TAŞINMAZ — o göç Slice 4'ün işi (plan
// §1 "Promote-and-delete into the new TableToolbar"). Bu dosya yalnız YENİ export'u kurar.
//
// `ListToolbar`'a göre iki ek alan: `summary` (sol, "8 of 11 ready" gibi özet — Stage 2
// "left: summary text-muted-foreground") ve `filters` (var olan durum/taşeron/saha/tarih
// `Select`leri için sağ slot — README'nin ÖMİT ettiği JENERİK "Filter/Export" düğmeleri
// DEĞİL, sayfanın KENDİ gerçek filtreleri, plan §4 "existing per-page filters ... move into
// the new TableToolbar, restyled — not deleted"). Arama YİNE controlled (sayfa debounce eder).
//
// **İKİ SATIR (S33 Slice 4 QA fix, HIGH — `S33-4-list-pages-qa.md` defekt #1).** `filters`
// eskiden arama+"New" ile AYNI `flex-nowrap` satırındaydı: sabit genişlikli `SelectMenu`ler
// (Permits'te 4 tane) + arama kutusu satırın KENDİSİNİ taşırıyordu (sayfa geneli yatay
// kaydırma) — `min-w-0` taşıyan özet `<p>` açığın TAMAMINI emip literal `width:0`'a düşüyordu
// (üç nokta bile görünmüyordu), arama kutusu 280px'ten 64px'e sıkışıyordu. Kalıcı düzeltme:
// `filters` ARTIK ayrı, kendi `flex-wrap` satırında (48px arama/aksiyon satırının ALTINDA) —
// filtre SAYISI ne olursa olsun arama kutusuyla ASLA aynı satırı paylaşmaz. Üst satırda yalnız
// özet + arama + "New" kalır; özete GERÇEK bir taban genişlik (`min-w-[6rem]`, tarayıcının asla
// ihlal etmediği `min-width` — CSS'in shrink-dağıtım matematiği DEĞİL) ve aramaya GERÇEK bir
// taban genişlik (`carbon:min-w-[10rem]`, temel 280px'ten aşağı inebilir ama okunaksız 64px'e
// asla düşmez) verildi.
//
// **Filtre satırı yatay, sabit-genişlikli hücreler (S33 Slice 4 QA fix, 2. tur — live QA
// bulgusu, YANLIŞ ÇIKTI — bkz. 3. tur).** İlk düzeltme filtreleri kendi satırına ayırdı ama HER
// filtrenin genişliğini açık bırakmıştı: `SelectMenu`'nün kendi tetikleyicisi `w-full` —
// sarmalayan bir genişlik vermeyince filtre, satırın TAMAMINI kaplayıp dikey olarak
// istifleniyordu (4 tam-genişlik satır + tarihler).
//
// **3. tur — `Children.map` çözümü YANLIŞTI, canlı ölçüm yakaladı.** 2. tur `filters`'ın HER
// doğrudan çocuğunu `Children.map` ile sarmalıyordu — ama tüketici sayfalar `filters`'ı TEK bir
// `<>…</>` fragment'i olarak geçiriyor, bu da TEK bir `Fragment` elemanına derlenir.
// `Children.map(filters, fn)` bu durumda `filters`'ı TEK çocuk sayar (Fragment'in KENDİSİ),
// İÇİNİ AÇMAZ — 6 filtre tek bir `w-48` kutusuna sarmalanıp kutunun İÇİNDE yine üst üste
// istifleniyordu. Kalıcı çözüm: TableToolbar `filters`'ı ARTIK sarmalamıyor (`{filters}`
// doğrudan render edilir); sarmalama sorumluluğu aşağıdaki `TableToolbarFilter`'a taşındı — HER
// tüketici HER filtre kontrolünü bununla AYRI AYRI sarmalamak ZORUNDA (tek filtreli sayfalar da
// dahil, tutarlılık için).

import { Plus, Rows4, Search } from 'lucide-react';
import type { ReactNode, Ref } from 'react';
import { Button } from './Button';
import { Input } from './Input';
import { Tooltip } from './Tooltip';
import { cn } from './cn';

/** `Table`in kabul ettiği iki yoğunluk (S34-2 design review §2 "lg sunulmaz"). */
export type TableToolbarDensity = 'sm' | 'md';

/**
 * Filtre satırındaki HER kontrolü ayrı ayrı sarmalar (`w-48` = 12rem sabit taban — dosya başı
 * notundaki Fragment sorununun çözümü). İçerideki `SelectMenu`nün `w-full` tetikleyicisi bu
 * 12rem'in YÜZDE 100'üne çözülür (global bir `[&>*]` seçicisiyle iç bileşenin sınıfını dışarıdan
 * "ele geçirmek" YERİNE — specificity/sıra kırılgan olurdu). `shrink-0` — birden çok filtre dar
 * bir viewport'ta SIKIŞMAK yerine bir SONRAKİ satıra SARAR (filtre satırı zaten `flex-wrap`).
 */
export function TableToolbarFilter({ children }: { children: ReactNode }) {
  return <div className="w-48 shrink-0">{children}</div>;
}

export function TableToolbar({
  summary,
  search,
  onSearchChange,
  searchPlaceholder,
  searchLabel,
  filters,
  density,
  onDensityChange,
  densityLabel,
  onNew,
  newLabel,
  newButtonRef,
  className,
}: {
  /** Sol taraf özet metni (ör. "8 / 11 hazır") — verilmezse boş bırakılır. */
  summary?: ReactNode;
  search?: string;
  onSearchChange?: (value: string) => void;
  searchPlaceholder?: string;
  /** Arama kutusunun erişilebilir etiketi (i18n ile) — `onSearchChange` verilirse zorunlu. */
  searchLabel?: string;
  /** Sayfanın kendi filtre `Select`leri (Filter/Export JENERİK düğmeleri DEĞİL — plan §4) —
   * KENDİ satırında, arama/aksiyon satırının altında render edilir (dosya başlığı notu). */
  filters?: ReactNode;
  /** Tablo satır yoğunluğu (S34-2 design review §2) — bu primitif depolamaya DOKUNMAZ, yalnız
   * görüntüler; sahiplik çağıranın `useTableDensity()`'sinde (`apps/web-expert/src/lib/uiPrefs.ts`).
   * `density`/`onDensityChange` İKİSİ de verilmezse düğme hiç render edilmez. */
  density?: TableToolbarDensity;
  onDensityChange?: (density: TableToolbarDensity) => void;
  /** Düğmenin erişilebilir adı (ör. "Kompakt satırlar") — `onDensityChange` verilirse zorunlu. */
  densityLabel?: string;
  onNew?: () => void;
  newLabel?: string;
  /** "Yeni" düğmesinin ref'i — çağıran, satır silinince/çekmece kapanınca odağı buraya indirebilsin. */
  newButtonRef?: Ref<HTMLButtonElement>;
  className?: string;
}) {
  return (
    <div className={cn('flex flex-col', className)}>
      <div
        className={cn(
          'flex flex-wrap items-center justify-between gap-3 px-1 py-2',
          'carbon:h-12 carbon:flex-nowrap carbon:gap-2 carbon:border-b carbon:border-border carbon:py-0 carbon:pl-4 carbon:pr-2',
        )}
      >
        {/* `min-w-[6rem]` (S33 QA fix) — GERÇEK bir taban; `min-w-0` (eski) shrink açığının
            TAMAMINI bu öğeye yükleyip literal `width:0` üretiyordu (dosya başlığı notu).
            `flex-1` kalan alanı doldurur, taban ALTINA tarayıcı ASLA inmez. */}
        <p className="min-w-[6rem] flex-1 truncate text-sm text-muted carbon:text-xs">{summary}</p>
        <div className="flex flex-wrap items-center gap-2 carbon:flex-nowrap carbon:gap-2">
          {onSearchChange ? (
            <div className="relative w-full max-w-xs carbon:w-[280px] carbon:min-w-[10rem] carbon:max-w-none">
              <Search
                className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted carbon:left-4"
                aria-hidden="true"
              />
              {/* `carbon:pl-12` (48px, README "icon at 16px, text starts ~48px") — `Input`'in
                  KENDİ `carbon:pl-4`'ünü `cn()`/`twMerge` üzerinden AYNI grupta (pl) çakıştırıp
                  override eder (bkz. Input.tsx'in dosya başlığı notu); base `pl-9` DOKUNULMAZ. */}
              <Input
                type="search"
                className="pl-9 carbon:h-12 carbon:pl-12"
                aria-label={searchLabel}
                placeholder={searchPlaceholder}
                value={search}
                onChange={(e) => onSearchChange(e.target.value)}
              />
            </div>
          ) : null}
          {density && onDensityChange ? (
            // Sıralı kapsam: arama daraltır, yoğunluk görüntüler, Yeni oluşturur (dosya başı
            // notu) — bu yüzden arama ile Yeni ARASINDA. `Button` DEĞİL, düz erişilebilir bir
            // `<button>`: basılı/basılı-değil için `Button`'ın varyant setine yeni bir görsel
            // durum eklemek yerine burada tek tüketicili, açık bir stil. `Tooltip` `asChild`
            // (M5, S34-3'te geldi) düğmeyi SARAR — `aria-label` erişilebilir adı hâlâ taşır,
            // tooltip yalnız fareyle/klavye odağında AYNI metni gösterir (tek kaynak, iki yüzey).
            <Tooltip asChild content={densityLabel} side="bottom">
              <button
                type="button"
                aria-pressed={density === 'sm'}
                aria-label={densityLabel}
                onClick={() => onDensityChange(density === 'sm' ? 'md' : 'sm')}
                className={cn(
                  'inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-md transition-colors',
                  'carbon:h-12 carbon:w-12 carbon:rounded-none',
                  density === 'sm'
                    ? 'bg-surface-muted text-fg carbon:bg-surface-hover'
                    : 'text-muted hover:bg-surface-muted carbon:hover:bg-surface-hover',
                )}
              >
                <Rows4 className="h-4 w-4" aria-hidden="true" />
              </button>
            </Tooltip>
          ) : null}
          {onNew ? (
            // `variant="ghost"` — README Stage 2 "TableToolbar": sağdaki aksiyonlar (Filter ·
            // Export · New…) `ghost h-12 px-4`, DOLU birincil DEĞİL. Ekranın TEK mavi dolu
            // butonu sayfa düzeyinde kalır (ör. Approve) — Button'ın `ghost` varyantı zaten
            // Carbon'da mavi METİN'dir (§2.4 "ghost = blue text", Slice 1a'da onaylandı); burada
            // YENİ bir varyant İCAT edilmiyor, var olanı doğru boyutla (`h-12`) kullanıyoruz.
            <Button
              ref={newButtonRef}
              variant="ghost"
              onClick={onNew}
              className="shrink-0 carbon:h-12 carbon:rounded-none"
            >
              <Plus className="h-4 w-4" aria-hidden="true" />
              {newLabel}
            </Button>
          ) : null}
        </div>
      </div>
      {filters ? (
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2 px-1 pb-2 carbon:border-b carbon:border-border carbon:px-4 carbon:py-2">
          {filters}
        </div>
      ) : null}
    </div>
  );
}
